import { useTranslation } from 'react-i18next'
import { Modal } from '../common/Modal'

interface DayReminder {
  medication: {
    _id: string
    name: string
    timeSlot: string
    specificTime?: string
    notes?: string
  } | null
  outcome: string
  completedAt?: number
  summary?: string
  caregiverNotifiedAt?: number
  retryCount: number
}

interface DayDetailModalProps {
  isOpen: boolean
  onClose: () => void
  date: string
  reminders: DayReminder[]
}

/**
 * Modal showing detailed medication reminder outcomes for a specific day
 */
export function DayDetailModal({ isOpen, onClose, date, reminders }: DayDetailModalProps) {
  const { t } = useTranslation()

  const reminderStyles: React.CSSProperties = {
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #f3f4f6',
  }

  const timeSlotStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '0.25rem',
  }

  const medicationNameStyles: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '0.5rem',
  }

  const outcomeStyles = (outcome: string): React.CSSProperties => {
    let color = '#6b7280'
    let bgColor = '#f3f4f6'

    if (outcome === 'taken' || outcome === 'taking_now') {
      color = '#15803d'
      bgColor = '#dcfce7'
    } else if (outcome === 'no_answer' || outcome === 'missed') {
      color = '#dc2626'
      bgColor = '#fee2e2'
    } else if (outcome === 'will_take_later') {
      color = '#a16207'
      bgColor = '#fef9c3'
    }

    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '13px',
      fontWeight: 500,
      color,
      backgroundColor: bgColor,
      marginBottom: '0.5rem',
    }
  }

  const summaryStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#4b5563',
    backgroundColor: '#f9fafb',
    padding: '0.75rem',
    borderRadius: '8px',
    fontStyle: 'italic',
    lineHeight: 1.5,
  }

  const notifiedStyles: React.CSSProperties = {
    fontSize: '12px',
    color: '#9ca3af',
    marginTop: '0.5rem',
  }

  const emptyStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '2rem',
    color: '#9ca3af',
  }

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })
  }

  const getTimeSlotLabel = (timeSlot: string): string => {
    const labels: Record<string, string> = {
      morning: t('medications.morning', 'Morning'),
      afternoon: t('medications.afternoon', 'Afternoon'),
      evening: t('medications.evening', 'Evening'),
      bedtime: t('medications.bedtime', 'Bedtime'),
    }
    return labels[timeSlot] || timeSlot
  }

  const getOutcomeLabel = (outcome: string, completedAt?: number): string => {
    const timeStr = completedAt
      ? new Date(completedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : ''

    switch (outcome) {
      case 'taken':
        return `✓ ${t('medications.takenAt', 'Taken at')} ${timeStr}`
      case 'taking_now':
        return `✓ ${t('medications.takenAt', 'Taken at')} ${timeStr}`
      case 'will_take_later':
        return `⏰ ${t('medications.willTakeLater', 'Will take later')}`
      case 'no_answer':
        return `✗ ${t('medications.missedNoAnswer', 'Missed (no answer after 3 attempts)')}`
      case 'missed':
        return `✗ ${t('medications.missed', 'Missed')}`
      default:
        return outcome
    }
  }

  const formatNotifiedTime = (timestamp: number): string => {
    const time = new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    return `${t('medications.caregiverNotified', 'Caregiver notified at')} ${time}`
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={formatDate(date)}>
      {reminders.length === 0 ? (
        <div style={emptyStyles}>{t('medications.noReminders', 'No reminders for this day')}</div>
      ) : (
        reminders.map((reminder, index) => (
          <div
            key={index}
            style={{
              ...reminderStyles,
              borderBottom: index === reminders.length - 1 ? 'none' : '1px solid #f3f4f6',
            }}
          >
            <div style={timeSlotStyles}>
              {reminder.medication
                ? getTimeSlotLabel(reminder.medication.timeSlot)
                : 'Unknown time'}
            </div>
            <div style={medicationNameStyles}>
              {reminder.medication?.name || 'Unknown medication'}
            </div>
            <div style={outcomeStyles(reminder.outcome)}>
              {getOutcomeLabel(reminder.outcome, reminder.completedAt)}
            </div>

            {reminder.summary && <div style={summaryStyles}>{reminder.summary}</div>}

            {reminder.caregiverNotifiedAt && (
              <div style={notifiedStyles}>{formatNotifiedTime(reminder.caregiverNotifiedAt)}</div>
            )}
          </div>
        ))
      )}
    </Modal>
  )
}
